// Names, help text and starting content for each service page block.
// The backend's type codes stay internal; admins see the labels.

export const API_BASE = (process.env.REACT_APP_GRAPHQL_URI || "https://api.barbera.in/graphql").replace(/\/graphql\/?$/, "");
export const img = (path) => (!path ? null : path.startsWith("http") ? path : `${API_BASE}${path}`);

export const BLOCK_TYPES = {
  MEDIA: { label: "Photos", help: "Swipeable photos. As the first block it becomes the full-width photo at the top of the page." },
  HIGHLIGHTS: { label: "Highlights", help: "Short claims with icons. Banner style adds a headline, colours and a photo, like a product launch." },
  OPTIONS: { label: "Options rail", help: "Lets the customer switch to a sibling service, for example the same area in every wax type." },
  INCLUDES: { label: "Package contents", help: "Automatic: the services inside a package. Hidden for services that are not packages." },
  STEPS: { label: "How it works", help: "Numbered steps with optional photos. Can use each service's own Procedure text." },
  BEFORE_AFTER: { label: "Before / after", help: "Two photos side by side." },
  PRODUCTS: { label: "Products used", help: "Products and brands, from the service's Products used, or your own list." },
  BULLETS: { label: "List (note, tips, benefits)", help: "Please note, aftercare, benefits or recommended for. Can use each service's own details text." },
  FAQ: { label: "FAQs", help: "Questions and answers, one open at a time." },
  INFO_SHEET: { label: "Info row → sheet", help: "A row like \"Know your wax type\" that opens a bottom sheet." },
  TRUST: { label: "Barbera promise", help: "Real totals from completed bookings and your promises." },
  REVIEWS: { label: "Ratings & reviews", help: "The service's rating summary, and customer reviews you pick." },
  ALSO_ADD: { label: "Frequently added together", help: "Services most often booked in the same order, or ones you pick." },
  INCLUDE_PAGE: { label: "Shared page", help: "Inserts all blocks of another page here, for content shared by many pages (common FAQs, promise)." },
};

export const OPTION_SOURCES = {
  VARIANTS: "Same service in other types (Full leg wax → every wax type)",
  SAME_CATEGORY: "Other services in the same category",
  SAME_PRODUCT: "Other services of the same product / brand",
  COLLECTION: "A home collection",
  SERVICES: "Services I pick",
};

export const DETAILS_KEYS = ["Procedure", "Benefits", "Recommended For", "Products Used", "Precautions", "Things To Know", "After Care Tips"];

export const BULLET_STYLES = {
  CHECK: "Ticks (benefits)",
  NOTE: "Please note (amber box)",
  TIPS: "Tips with icons",
  PLAIN: "Plain bullets",
};

export const ICONS = {
  check: "✓ Tick", leaf: "🌿 Natural", sparkle: "✨ Sparkle", shield: "🛡 Safe / hygienic", clock: "⏱ Time", drop: "💧 Moisture",
  sun: "☀ Sun / SPF", star: "★ Star", user: "🙂 Beautician", gift: "🎁 Gift", home: "🏠 Home", heart: "♥ Care", brush: "🖌 Makeup",
  spa: "🪷 Spa", ac: "❄ AC", ban: "⛔ Not allowed", info: "ℹ Info", thermo: "🌡 Temperature", hand: "✋ Hands",
};

export const DEFAULTS = {
  MEDIA: { config: { images: [], useServiceImage: true, aspect: "4:3" } },
  HIGHLIGHTS: { config: { style: "LIST", items: [{ icon: "shield", text: "" }] } },
  OPTIONS: { title: "Choose your option", config: { source: "VARIANTS", labelBy: "PRODUCT", max: 10 } },
  INCLUDES: { title: "What's included", config: {} },
  STEPS: { title: "How it works", config: { style: "TIMELINE", source: "DETAILS", detailsKeys: ["Procedure"], steps: [] } },
  BEFORE_AFTER: { title: "See the difference", config: {} },
  PRODUCTS: { title: "Products we use", config: { source: "SERVICE", items: [] } },
  BULLETS: { title: "Please note", config: { style: "NOTE", source: "DETAILS", detailsKeys: ["Precautions", "Things To Know"], items: [] } },
  FAQ: { title: "Frequently asked questions", config: { items: [{ q: "", a: "" }] } },
  INFO_SHEET: { config: { label: "", sheetTitle: "", body: "", items: [] } },
  TRUST: { title: "The Barbera promise", config: { showStats: true, points: [] } },
  REVIEWS: { title: "Ratings & reviews", config: { showSummary: true, orderIds: [] } },
  ALSO_ADD: { title: "Frequently added together", config: { source: "AUTO", max: 8 } },
  INCLUDE_PAGE: { config: { pageId: "" } },
};

let seq = 0;
export const newBlockId = () => `b${Date.now().toString(36)}${(seq += 1)}`;

export const newBlock = (type) => {
  const d = DEFAULTS[type] || { config: {} };
  return { id: newBlockId(), type, title: d.title || null, subtitle: null, isActive: true, config: JSON.parse(JSON.stringify(d.config)) };
};

/** One line describing a block in the list. */
export const blockSummary = (b) => {
  const c = b.config || {};
  switch (b.type) {
    case "MEDIA": return c.images?.length ? `${c.images.length} photo(s)` : "Service photo";
    case "HIGHLIGHTS": return `${(c.style || "LIST").toLowerCase()} · ${c.headline || `${(c.items || []).length} items`}`;
    case "OPTIONS": return OPTION_SOURCES[c.source] || c.source;
    case "STEPS": return c.source === "DETAILS" ? `From each service's ${(c.detailsKeys || ["Procedure"]).join(", ")}` : `${(c.steps || []).length} steps`;
    case "BULLETS": return c.source === "DETAILS" ? `From ${(c.detailsKeys || []).join(" + ")}` : `${(c.items || []).length} items`;
    case "FAQ": return `${(c.items || []).length} questions`;
    case "INFO_SHEET": return c.label || "No label yet";
    case "REVIEWS": return `${c.showSummary === false ? "No summary" : "Rating summary"} · ${(c.orderIds || []).length} reviews picked`;
    case "ALSO_ADD": return c.source === "SERVICES" ? `${(c.serviceIds || []).length} picked` : "Automatic from orders";
    case "PRODUCTS": return c.source === "CUSTOM" ? `${(c.items || []).length} products` : "From the service";
    default: return "";
  }
};
